/**
 * Chapter tree helpers (design 2026-10-31, C1). Pure, used by API and web.
 *
 * Chapter NUMBERS are not derived here: generated subsections are numbered
 * too, so the single numbering lives in `renderDocument` (document-render.ts).
 */
/** By `order` (fractions allowed), then title (German collation), then id. */
export function compareSections(a, b) {
    return a.order - b.order || a.title.localeCompare(b.title, 'de') || a.id.localeCompare(b.id);
}
/**
 * Flat stored sections -> ordered tree. A section whose parent is missing is
 * shown at the top level instead of being dropped (same rule as the location
 * tree): a chapter nobody can see is worse than one in the wrong place. A
 * section on a parent cycle is shown at the top level too, so corrupt data
 * cannot hang the editor.
 */
export function buildSectionTree(sections) {
    const nodes = new Map();
    for (const section of sections)
        nodes.set(section.id, { section, children: [] });
    const roots = [];
    for (const node of nodes.values()) {
        const parentId = node.section.parentSectionId;
        const parent = parentId === undefined ? undefined : nodes.get(parentId);
        if (parent === undefined || isOnCycle(node.section, nodes)) {
            roots.push(node);
        }
        else {
            parent.children.push(node);
        }
    }
    sortRecursively(roots);
    return roots;
}
/** Walks up the parents; the map size bounds the walk. */
function isOnCycle(section, nodes) {
    let currentId = section.parentSectionId;
    for (let steps = 0; currentId !== undefined && steps <= nodes.size; steps += 1) {
        if (currentId === section.id)
            return true;
        currentId = nodes.get(currentId)?.section.parentSectionId;
    }
    return false;
}
function sortRecursively(nodes) {
    nodes.sort((a, b) => compareSections(a.section, b.section));
    for (const node of nodes)
        sortRecursively(node.children);
}
/** Depth first, in tree order - the order of the table of contents. */
export function flattenSectionTree(tree) {
    return tree.flatMap((node) => [node.section, ...flattenSectionTree(node.children)]);
}
/** The section and all its descendants, found through the materialised path. */
export function sectionSubtree(sections, sectionId) {
    return sections.filter((section) => section.path.includes(sectionId));
}
/** How many levels the subtree spans: 1 for a leaf, 0 for an unknown id. */
export function subtreeHeight(sections, sectionId) {
    const subtree = sectionSubtree(sections, sectionId);
    if (!subtree.some((section) => section.id === sectionId))
        return 0;
    // Anchored on the id, not on the root's own path length: after an interrupted
    // multi-batch move the descendants already carry the new prefix.
    return Math.max(...subtree.map((section) => section.path.length - section.path.indexOf(sectionId)));
}
//# sourceMappingURL=section-tree.js.map